// Pestaña Cliente: identificación de la venta, teléfono de la ficha del cliente y su crédito SureKeep.
import { useTranslation } from 'react-i18next';
import { saleCustomerLabel } from '@/shared/lib/format/saleCustomer';
import { Link } from '@tanstack/react-router';
import { ArrowRight } from 'lucide-react';
import { Skeleton } from '@/kit';
import type { BackofficeSale } from '@/api/services/retail-wizard/contract';
import {
    DetailRow,
    PendingDetailRow,
} from '@/domains/sales/portal/mysales/components/SaleDetailSections';
import { creditHistoryCards } from '@/domains/sales/portal/mysales/lib/saleCustomerCredit';
import { formatPhone } from '@/domains/sales/portal/customers/lib/customersFormat';
import { PENDING_CELL } from '@/domains/sales/portal/mysales/lib/mySalesFormat';
import { useVentaCustomer } from '../../api/useVentas';
import { InfoRow, TAB_BODY, TabSection, dash } from './VentaTab';

/** `summary360` responde 403 al Admin VL: nivel e historial no se pueden leer todavía. */
export const CREDIT_NO_ACCESS_NOTE =
    'Tu rol todavía no puede leer el nivel ni el historial de crédito del cliente.';

export const SIGNUP_NO_SOURCE_NOTE =
    'La ficha del cliente guarda su primera compra, no la fecha en que se dio de alta.';

const CUSTOMER_UNREADABLE_NOTE = 'No se pudo leer la ficha del cliente.';

function PhoneRow({
    retailCustomerId,
    retailerId,
}: {
    retailCustomerId: string | null;
    retailerId: string | null;
}) {
    const customer = useVentaCustomer(retailCustomerId, retailerId);
    if (!retailCustomerId) {
        return (
            <PendingDetailRow
                label="Teléfono"
                reason="La venta no quedó ligada a una ficha de cliente."
            />
        );
    }
    if (customer.isLoading) return <Skeleton lines={1} className="my-1" />;
    if (customer.isError || !customer.data) {
        return <PendingDetailRow label="Teléfono" reason={CUSTOMER_UNREADABLE_NOTE} />;
    }
    return <DetailRow label="Teléfono" value={formatPhone(customer.data.phone)} />;
}

export function ClienteTab({
    sale,
    retailerId = null,
}: {
    sale: BackofficeSale;
    /** Cadena de la venta: la ficha del cliente se consulta dentro de ella. */
    retailerId?: string | null;
}) {
    const { t } = useTranslation('ventas');

    return (
        <div className={TAB_BODY}>
            <TabSection title={t('detalle.identificacion', { defaultValue: 'Identificación' })}>
                <InfoRow
                    label={t('detalle.nombre', { defaultValue: 'Nombre' })}
                    value={dash(saleCustomerLabel(sale))}
                />
                <InfoRow
                    label={t('detalle.curp', { defaultValue: 'CURP' })}
                    value={dash(sale.customerCurp)}
                    mono
                />
                <PhoneRow retailCustomerId={sale.retailCustomerId} retailerId={retailerId} />
                <PendingDetailRow label="Alta cliente" reason={SIGNUP_NO_SOURCE_NOTE} />
            </TabSection>

            <TabSection title="Nivel SureKeep">
                <p title={CREDIT_NO_ACCESS_NOTE} className="mb-1 text-muted-foreground">
                    {PENDING_CELL}
                </p>
            </TabSection>

            <TabSection title="Historial con SureKeep">
                <div className="mb-2 grid grid-cols-3 gap-2">
                    {creditHistoryCards(null).map((card) => (
                        <div
                            key={card.label}
                            title={CREDIT_NO_ACCESS_NOTE}
                            className="rounded-md border border-border p-2 text-center"
                        >
                            <div className="text-[9.5px] font-semibold uppercase tracking-wider text-muted-foreground">
                                {card.label}
                            </div>
                            <div className="text-[14px] font-semibold tabular-nums text-muted-foreground">
                                {PENDING_CELL}
                            </div>
                        </div>
                    ))}
                </div>
                <p className="mb-3 text-[11px] leading-relaxed text-muted-foreground">
                    {CREDIT_NO_ACCESS_NOTE}
                </p>
            </TabSection>

            {sale.retailCustomerId ? (
                <Link
                    to="/retail-admin/$section"
                    params={{ section: 'clientes' }}
                    search={{ cliente: sale.retailCustomerId }}
                    className="inline-flex items-center gap-1 text-[11.5px] text-primary hover:underline"
                >
                    {t('detalle.verFichaCompleta', { defaultValue: 'Ver ficha completa' })}
                    <ArrowRight className="size-3" aria-hidden />
                </Link>
            ) : null}
        </div>
    );
}
